// Unpublished Work © 2020-2026 Deere & Company.

import {SelectedFacets, facetKeys} from '../../state/struct/customer-search/facets/selected-facets';
import * as actions from '../../action-list';

const updateMultipleInputFacets = (state, action) => SelectedFacets.update(state, {
    $merge: action.facetsToUpdate
});

const updateFacet = (state, facetKey, newValue) => SelectedFacets.update(state, {
    [facetKey]: {
        $set: newValue
    }
});

const setSelectedFacets = (state, action) => {
    const validStateCodes = (action.facetOptions?.states || []).map((option) => option.key);
    const filteredStates = (action.searchPreferences.stateCodes || []).filter((code) => validStateCodes.includes(code));

    return SelectedFacets.update(state, {
        $merge: {
            country: action.searchPreferences.countryCode,
            states: filteredStates
        }
    });
};

const updateCountryFacet = (state, action) => SelectedFacets.update(state, {
    [facetKeys.COUNTRY]: {
        $set: action.country
    },
    [facetKeys.CITY]: {
        $set: ''
    },
    [facetKeys.CITIES]: {
        $set: []
    },
    [facetKeys.POSTAL_CODE]: {
        $set: ''
    }
});

const updateCustomerType = (state, action) => updateFacet(state, facetKeys.CUSTOMER_TYPES, action.selectedFacets);

const updateSearchQuery = (state, action) =>
    SelectedFacets.update(state, {
        query: {
            [action.queryType]: {
                $set: action.value
            }
        }
    });

const updateInputFacets = (state, action) => updateFacet(state, action.key, action.value);

const updateUniqueInputFacets = (state, action) => {
    const uniqueKeys = [
        facetKeys.DBS_NUMBER,
        facetKeys.REWARDS_NUMBER,
        facetKeys.CUSTOMER_ENTITY_ID
    ];

    const keysToClear = uniqueKeys.filter((key) => key !== action.key);

    const emptyValues = Object.fromEntries(
        keysToClear.map((keyToClear) => [
            [keyToClear], {
                $set: ''
            }
        ])
    );

    return SelectedFacets.update(state, {
        [action.key]: {
            $set: action.value
        },
        ...emptyValues
    });
};

const updateStatesFacet = (state, action) => updateFacet(state, facetKeys.STATES, action.states);

const updateCitiesFacet = (state, action) => updateFacet(state, facetKeys.CITIES, action.cities);

const resetToDefaultPreferences = (state, action) => new SelectedFacets({
    country: action.searchPreferences.countryCode,
    states: action.searchPreferences.stateCodes
});

export const selectedFacets = (state = new SelectedFacets({}), action = {}) => {
    const actionHandlers = {
        [actions.SET_CUSTOMER_SEARCH_FACETS]: setSelectedFacets,
        [actions.UPDATE_COUNTRY_TYPE_FACET]: updateCountryFacet,
        [actions.UPDATE_CUSTOMER_TYPE_FACET]: updateCustomerType,
        [actions.UPDATE_INPUT_TYPE_FACET]: updateInputFacets,
        [actions.UPDATE_UNIQUE_INPUT_TYPE_FACET]: updateUniqueInputFacets,
        [actions.UPDATE_MULTIPLE_INPUT_TYPE_FACETS]: updateMultipleInputFacets,
        [actions.UPDATE_SEARCH_QUERY]: updateSearchQuery,
        [actions.UPDATE_STATES_FACET]: updateStatesFacet,
        [actions.UPDATE_CITIES_FACET]: updateCitiesFacet,
        [actions.RESET_FACETS_TO_DEFAULTS]: resetToDefaultPreferences
    };
    const reducer = actionHandlers[action.type];

    return reducer ? reducer(state, action) : state;
};
